import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import Markdown from "../components/Markdown";
import { Library, Printer, Copy, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";

// Public, no-auth view of a shared plan. We use a bare axios instance (no
// credentials, no interceptor) so the public endpoint can be hit by anyone.
const publicGet = async (token) => {
  const r = await axios.get(`/api/plans/public/${token}`);
  return r.data;
};

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return iso; }
}

export default function PublicPlan() {
  const { token } = useParams();
  const [plan, setPlan] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    publicGet(token)
      .then((d) => { if (alive) { setPlan(d); setLoading(false); } })
      .catch((e) => {
        if (alive) {
          setError(e.response?.status === 404 ? "This share link is no longer active." : "Could not load plan");
          setLoading(false);
        }
      });
    return () => { alive = false; };
  }, [token]);

  if (loading) {
    return <div className="min-h-screen bg-[#0B0B0B] text-white grid place-items-center text-sm text-[#A3A3A3]">Loading…</div>;
  }
  if (error || !plan) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white grid place-items-center p-6">
        <div className="card-surface p-10 text-center max-w-md">
          <div className="w-14 h-14 mx-auto border border-amber-400/50 grid place-items-center mb-4">
            <AlertTriangle size={20} className="text-amber-400" />
          </div>
          <div className="font-display text-2xl font-black uppercase tracking-tight">Link not active</div>
          <p className="text-sm text-[#A3A3A3] mt-2">{error || "Ask your coach for a fresh link."}</p>
        </div>
      </div>
    );
  }

  const meta = [
    plan.lesson_type ? plan.lesson_type.replace("_", " ") : null,
    plan.duration_mins ? `${plan.duration_mins} min` : null,
    plan.courts ? `${plan.courts} courts` : null,
    plan.group_size ? `${plan.group_size} students` : null,
    plan.skill_level ? `DUPR ${plan.skill_level}` : null,
  ].filter(Boolean);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="public-plan-page">
      <Toaster theme="dark" />
      <header className="border-b border-white/10 print:hidden">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 font-display text-lg font-black uppercase tracking-tight">
            <Library size={16} className="text-[#38BDF8]" />
            <span>PBCOACH.APP</span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => { navigator.clipboard.writeText(plan.plan_text || ""); toast.success("Copied"); }}
              className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
              data-testid="public-plan-copy"
            >
              <Copy size={12} /> Copy
            </button>
            <button
              onClick={() => window.print()}
              className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
              data-testid="public-plan-print"
            >
              <Printer size={12} /> Print
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-10 print:py-0">
        <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">
          Lesson plan from {plan.coach_name || "Coach"}
        </div>
        <h1 className="font-display text-4xl sm:text-5xl font-black uppercase tracking-tighter mt-2">
          {plan.title}
        </h1>
        <div className="text-[11px] text-[#A3A3A3] uppercase tracking-wider mt-3 flex flex-wrap gap-3">
          {meta.map((m, i) => <span key={i}>{m}</span>)}
          {plan.created_at ? <span>· {formatDate(plan.created_at)}</span> : null}
        </div>
        {plan.focus && (
          <div className="border-l-2 border-[#38BDF8] pl-4 mt-6 text-sm text-white/85 whitespace-pre-line">
            {plan.focus}
          </div>
        )}
        <article className="prose prose-invert max-w-none text-sm mt-8 print:text-black">
          <Markdown>{plan.plan_text || ""}</Markdown>
        </article>
        <footer className="mt-12 border-t border-white/10 pt-4 text-[10px] uppercase tracking-widest text-[#A3A3A3] flex justify-between print:hidden">
          <span>Shared via PBCOACH.APP</span>
          <a href="https://pbcoach.app" className="text-[#38BDF8]">pbcoach.app</a>
        </footer>
      </main>
    </div>
  );
}
